/**
 * StageColumn — one droppable column: sticky header (name, count, admin
 * tools), own vertical scroll, cards. Open stages carry their colour as a
 * 3 px top bar; won/lost headers get a green / muted-red wash.
 */
import { useEffect, useRef, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Pencil } from 'lucide-react';
import { columnHeaderClass, DEFAULT_STAGE_COLOR } from '@/features/stages/stage-colors';
import type { StageAdmin } from '@/features/stages/use-stage-admin';
import { ColumnMenu } from './ColumnMenu';
import { DraggableLeadCard } from './LeadCard';
import type { LeadCard, Stage } from './types';

function InlineRename({
  stage,
  onSubmit,
  onCancel,
}: {
  stage: Stage;
  onSubmit: (name: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(stage.name);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  return (
    <form
      className="min-w-0 flex-1"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(value);
      }}
    >
      <input
        ref={ref}
        aria-label="Stage name"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => onSubmit(value)}
        onKeyDown={(e) => e.key === 'Escape' && onCancel()}
        className="block min-h-10 w-full rounded-md border border-navy bg-white px-2 text-[13px] font-semibold text-gray-900"
      />
    </form>
  );
}

export function StageColumn({
  stage,
  leads,
  index,
  total,
  admin,
  onOpenLead,
}: {
  stage: Stage;
  leads: LeadCard[];
  index: number;
  total: number;
  /** Present only for company_admin+; gates every management control. */
  admin: StageAdmin | null;
  onOpenLead: (id: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id, data: { stage } });
  const [renaming, setRenaming] = useState(false);
  const renameButtonRef = useRef<HTMLButtonElement>(null);
  // Leaving the inline editor unmounts the focused input; hand focus back to
  // the pencil (next frame, once it is in the DOM again) so keyboard users
  // are not dropped to <body> (WCAG 2.4.3).
  const stopRenaming = () => {
    setRenaming(false);
    requestAnimationFrame(() => renameButtonRef.current?.focus());
  };
  const topBar = stage.kind === 'open' ? stage.color ?? DEFAULT_STAGE_COLOR : 'transparent';

  return (
    <section
      ref={setNodeRef}
      aria-label={`${stage.name}, ${leads.length} candidates`}
      data-testid={`column-${stage.id}`}
      className={[
        'flex h-full w-[272px] min-w-[272px] shrink-0 flex-col rounded-lg border transition-colors',
        isOver ? 'border-navy bg-navy/5' : 'border-gray-200/80 bg-gray-200/40',
      ].join(' ')}
    >
      {/*
        The name wraps to two lines instead of truncating ("Uncontacted Le…" at
        272 px, Stage 7 QA). The heading always reserves two lines (min-h-10)
        and the rename input is the same 40 px, so the sticky header keeps one
        height across columns and while renaming. The badge and the admin
        tools sit on the first line (items-start; the 40 px buttons pull up
        with negative margins so they centre on it).
      */}
      <header
        className={`sticky top-0 z-10 rounded-t-lg border-b px-3 pb-2 pt-3 ${columnHeaderClass(stage.kind)}`}
        style={{ boxShadow: `inset 0 3px 0 0 ${topBar}` }}
      >
        <div className="flex items-start gap-1.5">
          {renaming && admin ? (
            <InlineRename
              stage={stage}
              onSubmit={(name) => {
                stopRenaming();
                admin.rename(stage, name);
              }}
              onCancel={stopRenaming}
            />
          ) : (
            <h2
              tabIndex={-1}
              data-stage-heading={stage.id}
              className="line-clamp-2 min-h-10 min-w-0 flex-1 break-words rounded-sm text-[13px] font-semibold leading-5 text-gray-900"
              title={stage.name}
            >
              {stage.name}
            </h2>
          )}
          <span
            className="rounded-full bg-white/80 px-1.5 py-0.5 text-[12px] font-semibold leading-4 tabular-nums text-gray-700 ring-1 ring-gray-200"
            data-testid={`count-${stage.id}`}
          >
            {leads.length}
          </span>
          {admin && !renaming && (
            <>
              <button
                type="button"
                ref={renameButtonRef}
                onClick={() => setRenaming(true)}
                aria-label={`Rename ${stage.name}`}
                className="-mb-3 -mt-2 flex h-10 w-10 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              >
                <Pencil size={14} strokeWidth={2} aria-hidden />
              </button>
              <ColumnMenu
                stage={stage}
                index={index}
                total={total}
                count={leads.length}
                admin={admin}
                onRename={() => setRenaming(true)}
              />
            </>
          )}
        </div>
      </header>

      <div className="scrollbar-thin flex-1 space-y-2 overflow-y-auto p-2 overscroll-contain">
        {leads.map((lead) => (
          <DraggableLeadCard key={lead.id} lead={lead} onOpen={onOpenLead} />
        ))}
        {leads.length === 0 && (
          <div className="rounded-md border border-dashed border-gray-300 px-3 py-6 text-center text-[12px] text-muted">
            {isOver ? 'Drop here' : 'No candidates'}
          </div>
        )}
      </div>
    </section>
  );
}
